import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import type { MyPayEnvironment } from "../../generated/core-api";
import {
  fetchOnboardingCompanyLookup,
  OnboardingCompanyNotFoundError,
  ONBOARDING_COMPANY_LOOKUP_UNEXPECTED_SHAPE,
} from "./onboardingCompanyLookup";

export interface CompanyCodeValidationResult {
  valid: boolean;
  companyName?: string;
}

// 403 means the caller's token is valid but lacks permission for this company.
// Handled separately from not-found because the user message must reference
// access/permissions, not "code not found".
const FORBIDDEN_STATUS = 403;

async function validateCompanyCodeApi(
  code: string,
  mypayEnvironment: MyPayEnvironment
): Promise<CompanyCodeValidationResult> {
  const trimmed = code.trim();
  if (!trimmed) {
    return { valid: false };
  }

  try {
    const lookup = await fetchOnboardingCompanyLookup(trimmed, mypayEnvironment);
    return { valid: true, companyName: lookup.companyName };
  } catch (error) {
    if (error instanceof OnboardingCompanyNotFoundError) {
      return { valid: false };
    }
    throw error;
  }
}

export interface UseCompanyCodeValidationReturn {
  /** The validated company code, or null if not yet validated. */
  validatedCode: string | null;
  /** Datapay environment the current validation applies to, if any. */
  validatedEnvironment: MyPayEnvironment | null;
  /** Human-readable company name returned by the API, if available. */
  companyName: string | null;
  isValidating: boolean;
  /** True when the last validation call returned valid=true. */
  isValid: boolean;
  /** Error message if the last validation call failed or returned valid=false. */
  validationError: string | null;
  /**
   * Validate the given company code against the Christmas API.
   * Sets validatedCode on success, validationError on failure.
   */
  validateCode: (code: string) => Promise<void>;
  /** Reset all validation state (call when the user clears the input). */
  reset: () => void;
}

/**
 * Manages company code input validation against the Christmas API.
 *
 * Shared across bulk import flows (Work Patterns, Pay Component Import, and
 * future modules): the validated company code (`validatedCode`) gates downstream
 * CSV/import steps and is passed to workflow invocations. This is the single
 * company-validation authority — do not fork a per-module copy.
 *
 * @param mypayEnvironment Target MyPay deployment for the lookup. Must match
 *   the environment selected for workflow invocations. When null, validation is
 *   skipped (no API call).
 * @param validateFn Injectable validator. Defaults to the real API call
 *   (`validateCompanyCodeApi`) so callers need not pass anything. Tests inject a
 *   stub to exercise the `valid:false`, network-error, and out-of-order race
 *   branches without hitting the network.
 */
export function useCompanyCodeValidation(
  mypayEnvironment: MyPayEnvironment | null,
  validateFn: (
    code: string,
    mypayEnvironment: MyPayEnvironment
  ) => Promise<CompanyCodeValidationResult> = validateCompanyCodeApi
): UseCompanyCodeValidationReturn {
  const [validatedCode, setValidatedCode] = useState<string | null>(null);
  const [validatedEnvironment, setValidatedEnvironment] =
    useState<MyPayEnvironment | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Latest-request-wins guard. Each call bumps the id; an async result is
  // ignored if a newer request has started since it was issued. Without this,
  // two rapid calls can resolve out of order and let a stale valid/invalid
  // result clobber a newer one — unlocking the dropzone for the wrong code.
  const requestIdRef = useRef(0);
  const mypayEnvironmentRef = useRef(mypayEnvironment);
  mypayEnvironmentRef.current = mypayEnvironment;

  // Drop stale validation when the user switches Datapay environment — a code
  // valid in nz-datapay may not exist (or may resolve differently) in nz-easipay.
  useEffect(() => {
    requestIdRef.current += 1;
    setValidatedCode(null);
    setValidatedEnvironment(null);
    setCompanyName(null);
    setIsValidating(false);
    setIsValid(false);
    setValidationError(null);
  }, [mypayEnvironment]);

  const validateCode = async (code: string): Promise<void> => {
    const trimmed = code.trim();

    if (!mypayEnvironmentRef.current) {
      requestIdRef.current += 1;
      setValidatedCode(null);
      setValidatedEnvironment(null);
      setCompanyName(null);
      setIsValidating(false);
      setIsValid(false);
      setValidationError(
        "Select a Datapay environment before validating the company code."
      );
      return;
    }

    if (!trimmed) {
      // Synchronous rejection still supersedes any in-flight request.
      requestIdRef.current += 1;
      setValidatedCode(null);
      setValidatedEnvironment(null);
      setCompanyName(null);
      setIsValidating(false);
      setIsValid(false);
      setValidationError("Company code is required.");
      return;
    }

    const requestId = ++requestIdRef.current;

    setIsValidating(true);
    setValidationError(null);
    setValidatedCode(null);
    setValidatedEnvironment(null);
    setIsValid(false);

    const environment = mypayEnvironmentRef.current;

    try {
      const result = await validateFn(trimmed, environment);

      // A newer validateCode call has superseded this one — drop the result.
      if (requestId !== requestIdRef.current) return;

      if (result.valid) {
        setValidatedCode(trimmed);
        setValidatedEnvironment(environment);
        setCompanyName(result.companyName ?? null);
        setIsValid(true);
      } else {
        setValidatedCode(null);
        setValidatedEnvironment(null);
        setCompanyName(null);
        setIsValid(false);
        setValidationError("There is no Company Code match");
      }
    } catch (error: unknown) {
      if (requestId !== requestIdRef.current) return;
      setValidatedCode(null);
      setValidatedEnvironment(null);
      setCompanyName(null);
      setIsValid(false);

      if (
        isAxiosError(error) &&
        error.response?.status === 401
      ) {
        // UnauthorizedInterceptor owns re-auth — do not swallow as a field error.
        throw error;
      } else if (
        error instanceof Error &&
        error.message === ONBOARDING_COMPANY_LOOKUP_UNEXPECTED_SHAPE
      ) {
        setValidationError(
          "Company lookup returned an unexpected response. Please try again shortly."
        );
      } else if (
        isAxiosError(error) &&
        error.response?.status === FORBIDDEN_STATUS
      ) {
        setValidationError(
          `You don't have access to company "${trimmed}". Please contact your administrator.`
        );
      } else if (
        isAxiosError(error) &&
        error.response != null &&
        error.response.status >= 500
      ) {
        setValidationError(
          "The server is temporarily unavailable. Please try again shortly."
        );
      } else {
        setValidationError(
          "Could not reach the server. Please check your connection and try again."
        );
      }
    } finally {
      // Only the latest request may clear the validating flag, so an older
      // resolution can't flip it off while a newer request is still running.
      if (requestId === requestIdRef.current) {
        setIsValidating(false);
      }
    }
  };

  const reset = () => {
    // Invalidate any in-flight validation so its result can't repopulate state
    // after the user has cleared the input.
    requestIdRef.current += 1;
    setValidatedCode(null);
    setValidatedEnvironment(null);
    setCompanyName(null);
    setIsValidating(false);
    setIsValid(false);
    setValidationError(null);
  };

  return {
    validatedCode,
    validatedEnvironment,
    companyName,
    isValidating,
    isValid,
    validationError,
    validateCode,
    reset,
  };
}
